import cx from "classnames";
import range from "lodash-es/range";
import React from "react";
import { CellMeasurer, CellMeasurerCache, ListRowProps } from "react-virtualized";

import { Comment, CommentForumPlaceholder } from "@/mk2/apps/blogs/components/Comment";
import { Props } from "@/mk2/apps/forum/containers/FeedUntrustworthyUser/FeedUntrustworthyUserPage";
import { postDetailUrl } from "@/mk2/apps/forum/helpers";
import { forumTopicsUrl } from "@/mk2/apps/forum/urls";
import { InfiniteListLoadable } from "@/mk2/components/InfiniteListLoadable";
import { Link } from "@/mk2/components/Link";
import {
    UntrustworthyUserAlert,
    UntrustworthyUserAlertPlaceholder,
} from "@/mk2/components/UntrustworthyUserAlert";
import { Failure } from "@/mk2/components/errors/Failure";
import { LoadingSwitch } from "@/mk2/containers/LoadingSwitch/LoadingSwitch";
import { LoadingState } from "@/mk2/reducers/loadingState";
import { CommentEntity } from "@/mk2/schemas";
import { BACK_TO_TOPICS, IN_DISCUSSION } from "@/mk/autogenerated/translations/FeedUntrustworthyUser.acc9a82026c46dfca0f96b527f298cc2";

import styles from "./FeedUntrustworthyUser.mscss";

interface State {
    client: boolean;
}

class FeedUntrustworthyUser extends React.Component<Props, State> {
    public state: State = {
        client: false,
    };

    private cache = new CellMeasurerCache({
        defaultHeight: 100,
        fixedWidth: true,
    });

    public shouldComponentUpdate(n: Props) {
        const p = this.props;

        return (
            p.comments !== n.comments || p.after !== n.after || p.loadingState !== n.loadingState
        );
    }

    public componentDidMount(): void {
        this.setState({ client: true });
    }

    public componentDidUpdate(
        prevProps: Readonly<Props>,
        prevState: Readonly<{}>,
        snapshot?: any,
    ): void {
        if (prevProps.comments.length !== this.props.comments.length) {
            this.props.comments.forEach((comment, index) => {
                if (!prevProps.comments[index] || comment.id !== prevProps.comments[index].id) {
                    this.cache.clear(index, 0);
                }
            });
        }
    }

    public render() {
        const { loadingState, comments } = this.props;

        return (
            <div className={styles.FeedUntrustworthyUser}>
                <LoadingSwitch
                    loadingState={loadingState}
                    onLoad={this.load}
                    onRenderInit={this.renderInit}
                    onRenderSuccess={this.renderSuccess}
                    onRenderFailure={this.renderFailure}
                    hasData={comments && comments.length > 0}
                />
            </div>
        );
    }

    private renderInit = () => {
        return (
            <>
                <UntrustworthyUserAlertPlaceholder />
                {
                    // vykreslit dostatok elementov, tak aby browser nezmensil scrollTop position
                    // pocas LoadingState.LOADING pretoze vyska body je mensia nez scrollTop pozicia
                    // ktoru sa snazime zachovat
                    range(10).map((i) => (
                        <CommentForumPlaceholder
                            key={`placeholder-${i}`}
                            className={styles.PostDetail__commentPlaceholder}
                        />
                    ))
                }
            </>
        );
    };

    private renderSuccess = () => {
        const { comments, user, hasNext } = this.props;

        if (!comments.length) {
            return <UntrustworthyUserAlert user={user} />;
        }

        const rowCount = hasNext ? comments.length + 1 : comments.length;

        return (
            <>
                <UntrustworthyUserAlert user={user} />
                <InfiniteListLoadable
                    isRowLoaded={this.isRowLoaded}
                    loadMoreRows={this.loadNext}
                    rowCount={rowCount}
                    rowRenderer={this.renderRow}
                    rowHeight={this.cache.rowHeight}
                    estimatedRowHeight={100}
                    deferredMeasurementCache={this.cache}
                />
            </>
        );
    };

    private renderFailure = () => {
        const { errorMessage } = this.props;

        return (
            <Failure
                errorMessage={errorMessage}
                onRetry={this.load}
                backButtonUrl={forumTopicsUrl}
                backButtonLabel={BACK_TO_TOPICS}
            />
        );
    };

    private load = () => {
        const {
            routeParams: { username },
            onLoad,
            after,
        } = this.props;
        onLoad(username, after);
    };

    private loadNext = () => {
        const {
            onLoad,
            routeParams: { username },
            comments,
            loadingState,
        } = this.props;

        // Do not trigger next load
        if (loadingState === LoadingState.LOADING) {
            return;
        }

        const after = comments[comments.length - 1].id;
        onLoad(username, after);

        return new Promise((resolve) => {
            setTimeout(resolve, 500);
        });
    };

    private isRowLoaded = ({ index }) => !this.props.hasNext || index < this.props.comments.length;

    private renderRow = (listRowProps: ListRowProps) => {
        const {
            comments,
            posts,
            requestUser,
            requestUserPermissions,
            isMobile,
            location,
            routeNavigate,
        } = this.props;
        const { index, key, style, parent } = listRowProps;

        const isLoaded = this.isRowLoaded({ index });
        const comment: CommentEntity | null = comments[index] || null;
        const post = isLoaded ? posts.find((p) => p.id === comment.postId) : null;

        return (
            <CellMeasurer
                cache={this.cache}
                columnIndex={0}
                key={key}
                parent={parent}
                rowIndex={index}
            >
                <div
                    style={{
                        ...style,
                        // Fix SSR rendering, because we are using dynamic row heights
                        ...(!this.state.client ? { position: undefined } : null),
                    }}
                >
                    <div className={styles.FeedUntrustworthyUser__row}>
                        {isLoaded ? (
                            <Comment
                                className={cx(styles.FeedUntrustworthyUser__comment)}
                                comment={comment}
                                requestUser={requestUser}
                                requestUserPermissions={requestUserPermissions}
                                isMobile={isMobile}
                                inForum
                                followLinks={false}
                                moderators={
                                    post.topic.topicModerators?.map((tm) => tm.user) ||
                                    post.topic.moderators
                                }
                                location={location}
                                routeNavigate={routeNavigate}
                                highlightTriggered={false}
                                hasBodyHiddenText={false}
                                hasBodyDeletedStatus={false}
                                hasFooterLike={false}
                                hasFooterReply={false}
                                hasMenu={false}
                                hasMenuReport={false}
                                hasMenuEdit={false}
                                hasMenuDelete={false}
                                hasMenuMoveUp={false}
                                showUntrustwortyUserAlert={false}
                                onReply={this.noop}
                                onReport={this.noop}
                                onDelete={this.noop}
                                onSendHeart={this.noop}
                                post={post}
                                afterBody={
                                    <div
                                        className={
                                            styles.FeedUntrustworthyUser__comment__navigation
                                        }
                                    >
                                        {IN_DISCUSSION}
                                        {": "}
                                        <Link to={postDetailUrl(post)}>{post.heading}</Link>
                                    </div>
                                }
                            />
                        ) : (
                            <CommentForumPlaceholder />
                        )}
                    </div>
                </div>
            </CellMeasurer>
        );
    };

    private noop = () => {
        /* noope for Comment component */
    };
}

export default FeedUntrustworthyUser;
